/* import __COLOCATED_TEMPLATE__ from './task-suggestion-steps.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';

interface Signature {
  Args: {
    suggestion: TaskSuggestion;
  };
}

export default class TaskSuggestionSteps extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @tracked isApiDocsSideDrawerOpen = false;
  @tracked openSectionId: string | null = null;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    if (this.shouldAutoOpenApiDocs) {
      this.isApiDocsSideDrawerOpen = true;
      this.openSectionId = '2'; // ID of the section with the API docs button
    }
  }

  @action
  onOpenSectionChange(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  openApiSidePanel() {
    this.isApiDocsSideDrawerOpen = true;
  }

  @action
  closeApiSidePanel() {
    this.isApiDocsSideDrawerOpen = false;

    // Note: We don't clear query params here - they get cleared when navigating between jobs
    // This preserves the shareable URL behavior while preventing unexpected reopening
  }

  @action
  navigateToTasks() {
    this.router.transitionTo('apps.app.automation.fin-ai-agent.tasks');
  }

  @action
  navigateToDataConnectors() {
    this.router.transitionTo('apps.app.settings.app-settings.custom-actions');
  }

  get difficultyScoreText() {
    let score = this.args.suggestion.implementationDifficultyScore;
    if (!score) {
      return '';
    }
    if (score <= 2) {
      return this.intl.t('automation.analyze.task-suggestions.effort-score.low');
    } else if (score <= 4) {
      return this.intl.t('automation.analyze.task-suggestions.effort-score.medium');
    } else {
      return this.intl.t('automation.analyze.task-suggestions.effort-score.high');
    }
  }

  get difficultyScoreClasses() {
    let score = this.args.suggestion.implementationDifficultyScore;
    if (!score) {
      return '';
    }
    if (score <= 2) {
      return 'bg-beta-container border-beta-border';
    } else if (score <= 4) {
      return 'bg-notice-container border-notice-border';
    } else {
      return 'bg-error-container border-error-border';
    }
  }

  get shouldAutoOpenApiDocs() {
    let queryParams = this.router.currentRoute?.queryParams;
    let suggestionId = queryParams?.suggestionId;
    let showApiDocs = queryParams?.showApiDocs;

    return suggestionId === this.args.suggestion.id && showApiDocs === 'true';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TaskSuggestions::TaskSuggestionSteps': typeof TaskSuggestionSteps;
    'reporting/automation/fin-ai-agent/analyze/task-suggestions/task-suggestion-steps': typeof TaskSuggestionSteps;
  }
}
